import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useShop } from "../contexts/ShopContext";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Search, Plus, Trash2, Package, Eye, RotateCcw, IndianRupee, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

const PAY_METHODS = [
  { id: "cash", label: "Cash" },
  { id: "gpay", label: "GPay" },
  { id: "mpay", label: "MPay" },
  { id: "card", label: "Card" },
  { id: "bank", label: "Bank Transfer" },
];

export default function BulkOrders() {
  const { activeShop, activeShopId } = useShop();
  const [tab, setTab] = useState("new");

  // Order form state
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]); // {product_id, name, rate, qty}
  const [customer, setCustomer] = useState({ name: "", phone: "", address: "" });
  const [discount, setDiscount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [notes, setNotes] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");

  // List state
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewOrder, setViewOrder] = useState(null);

  // Reports state
  const [period, setPeriod] = useState("month");
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [report, setReport] = useState({ by_method: {}, products: [], buckets: [], order_count: 0, total_revenue: 0 });

  const loadProducts = async () => {
    const r = await api.get("/products");
    setProducts(r.data);
  };

  const loadOrders = async () => {
    const r = await api.get(`/bulk-orders?shop_id=${activeShopId}&limit=300`);
    setOrders(r.data);
  };

  const loadReport = async () => {
    const params = new URLSearchParams({ period, shop_id: activeShopId, year: String(year) });
    if (period === "month") params.set("month", String(month));
    const r = await api.get(`/bulk-orders/reports/period?${params.toString()}`);
    setReport(r.data);
  };

  useEffect(() => { loadProducts(); }, []);
  useEffect(() => { if (activeShopId) loadOrders(); }, [activeShopId]);
  useEffect(() => { if (activeShopId) loadReport(); }, [activeShopId, period, year, month]);

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const allowed = activeShop?.allowed_categories || [];
    let base = products;
    if (allowed.length > 0) {
      const allowedLower = allowed.map((c) => c.toLowerCase());
      base = products.filter((p) => allowedLower.includes((p.category || "").toLowerCase()));
    }
    if (!q) return base.slice(0, 50);
    return base.filter((p) =>
      p.name.toLowerCase().includes(q) || p.barcode?.toLowerCase().includes(q)
    ).slice(0, 50);
  }, [search, products, activeShop]);

  const addToCart = (p) => {
    setCart((c) => {
      const idx = c.findIndex((x) => x.product_id === p.product_id);
      if (idx >= 0) {
        const n = [...c]; n[idx] = { ...n[idx], qty: n[idx].qty + 10 }; return n;
      }
      return [...c, { product_id: p.product_id, name: p.name, rate: p.retail_price || p.mrp, qty: 10 }];
    });
  };

  const subtotal = cart.reduce((s, x) => s + x.rate * x.qty, 0);
  const netTotal = Math.max(0, Math.round(subtotal - Number(discount || 0)));

  const submitOrder = async () => {
    if (cart.length === 0) { toast.error("Add at least one item"); return; }
    try {
      const res = await api.post("/bulk-orders", {
        shop_id: activeShopId,
        items: cart,
        customer_name: customer.name, customer_phone: customer.phone, customer_address: customer.address,
        discount: Number(discount || 0),
        payments: [{ method: paymentMethod, amount: netTotal }],
        payment_method: paymentMethod,
        notes, delivery_date: deliveryDate,
      });
      toast.success(`Bulk Order ${res.data.order_no} created`);
      setCart([]); setCustomer({ name: "", phone: "", address: "" }); setDiscount(0); setNotes(""); setDeliveryDate("");
      loadOrders(); loadProducts();
      setTab("list");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk order failed");
    }
  };

  const refundOrder = async (id) => {
    if (!window.confirm("Refund this entire bulk order? Stock will be restored.")) return;
    try { await api.post(`/bulk-orders/${id}/refund`); toast.success("Refunded"); setViewOrder(null); loadOrders(); loadProducts(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
  };

  const filteredOrders = orders.filter((o) => statusFilter === "all" || o.status === statusFilter);

  return (
    <div className="p-8 overflow-y-auto" data-testid="bulk-orders-page">
      <div className="mb-6">
        <div className="label-overline text-muted-foreground">Wholesale &amp; Bulk</div>
        <h1 className="font-heading text-4xl font-bold text-ink flex items-center gap-2"><Package size={28} className="text-terracotta"/>Bulk Orders</h1>
        <p className="text-sm text-muted-foreground mt-1">Separate from daily POS — independent stock movements, no LP, separate reports.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-surface-alt">
          <TabsTrigger value="new" data-testid="tab-bulk-new">+ New Bulk Order</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-bulk-list">Order List ({orders.length})</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-bulk-reports">Reports</TabsTrigger>
        </TabsList>

        {/* NEW ORDER */}
        <TabsContent value="new" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Product picker */}
            <Card className="lg:col-span-2 border-border shadow-none">
              <CardContent className="p-4 space-y-3">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
                  <Input placeholder="Search products" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="bulk-product-search"/>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[500px] overflow-y-auto scroll-thin">
                  {filteredProducts.map((p) => {
                    const stock = p.stock?.[activeShopId] || 0;
                    return (
                      <button
                        key={p.product_id}
                        onClick={() => addToCart(p)}
                        data-testid={`bulk-prod-${p.product_id}`}
                        className="text-left bg-white border border-border rounded p-2 hover:border-terracotta hover:bg-terracotta/5 transition-colors"
                      >
                        <div className="text-xs font-medium line-clamp-2">{p.name}</div>
                        <div className="flex justify-between mt-1">
                          <span className="font-mono font-bold text-terracotta">₹{Math.round(p.retail_price)}</span>
                          <span className="text-[10px] text-muted-foreground">{stock} left</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Cart + customer */}
            <Card className="border-border shadow-none">
              <CardContent className="p-4 space-y-3">
                <div className="font-heading font-bold">Bulk Cart</div>
                <Input placeholder="Customer Name" value={customer.name} onChange={(e) => setCustomer({...customer, name: e.target.value})} data-testid="bulk-cust-name"/>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Phone" value={customer.phone} onChange={(e) => setCustomer({...customer, phone: e.target.value})} data-testid="bulk-cust-phone"/>
                  <Input placeholder="Address" value={customer.address} onChange={(e) => setCustomer({...customer, address: e.target.value})}/>
                </div>
                <Input type="date" placeholder="Delivery Date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} data-testid="bulk-delivery"/>
                <Input placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)}/>

                <div className="border border-border rounded max-h-72 overflow-y-auto scroll-thin">
                  {cart.length === 0 && <div className="p-4 text-center text-xs text-muted-foreground">Click products to add</div>}
                  {cart.map((it, idx) => (
                    <div key={idx} className="p-2 border-b border-border" data-testid={`bulk-cart-row-${idx}`}>
                      <div className="flex justify-between items-start gap-2">
                        <div className="text-xs font-medium flex-1">{it.name}</div>
                        <button onClick={() => setCart(cart.filter((_, i) => i !== idx))} className="text-destructive">
                          <Trash2 size={12}/>
                        </button>
                      </div>
                      <div className="flex gap-2 mt-1 items-center">
                        <Input
                          type="number" min="1"
                          value={it.qty}
                          onChange={(e) => {
                            const n = [...cart]; n[idx] = { ...n[idx], qty: Math.max(1, Number(e.target.value) || 1) }; setCart(n);
                          }}
                          className="w-20 h-7 text-center font-mono text-xs"
                          data-testid={`bulk-qty-${idx}`}
                        />
                        <span className="text-xs">× ₹</span>
                        <Input
                          type="number"
                          value={it.rate}
                          onChange={(e) => {
                            const n = [...cart]; n[idx] = { ...n[idx], rate: Number(e.target.value) || 0 }; setCart(n);
                          }}
                          className="w-24 h-7 text-right font-mono text-xs"
                          data-testid={`bulk-rate-${idx}`}
                        />
                        <span className="ml-auto font-mono font-bold text-xs">₹{Math.round(it.rate * it.qty)}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">₹{Math.round(subtotal)}</span></div>
                  <div className="flex justify-between items-center">
                    <span>Discount</span>
                    <Input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} className="w-24 h-7 text-right font-mono" data-testid="bulk-discount"/>
                  </div>
                  <div className="flex justify-between text-xl font-bold border-t border-border pt-1 mt-1">
                    <span>Net Total</span>
                    <span className="font-mono text-terracotta" data-testid="bulk-net-total">₹{Math.round(netTotal)}</span>
                  </div>
                </div>

                <select
                  className="w-full h-9 px-2 border border-border rounded text-sm"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  data-testid="bulk-pay-method"
                >
                  {PAY_METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>

                <Button
                  disabled={cart.length === 0}
                  onClick={submitOrder}
                  className="w-full bg-terracotta hover:bg-terracotta-hover text-white h-11 font-bold"
                  data-testid="bulk-submit"
                >
                  Create Bulk Order · ₹{Math.round(netTotal)}
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ORDER LIST */}
        <TabsContent value="list" className="mt-4">
          <div className="flex justify-end mb-3">
            <select
              className="h-9 px-2 border border-border rounded text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="bulk-status-filter"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
          <Card className="border-border shadow-none">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-surface-alt">
                    <TableHead>Order No</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead className="text-right">Items</TableHead>
                    <TableHead>Pay</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((o) => (
                    <TableRow key={o.order_id} data-testid={`bulk-row-${o.order_id}`}>
                      <TableCell className="font-mono text-xs">{o.order_no}</TableCell>
                      <TableCell className="text-xs">{new Date(o.created_at).toLocaleString()}</TableCell>
                      <TableCell className="text-sm">{o.customer_name || "-"} <span className="text-xs text-muted-foreground">{o.customer_phone}</span></TableCell>
                      <TableCell className="text-right">{o.items.length}</TableCell>
                      <TableCell className="uppercase text-xs">{o.payment_method}</TableCell>
                      <TableCell><Badge variant={o.status === "refunded" ? "destructive" : "outline"} className={o.status === "completed" ? "border-moss text-moss" : ""}>{o.status}</Badge></TableCell>
                      <TableCell className="text-right font-mono font-bold">₹{Math.round(o.net_total)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => setViewOrder(o)} data-testid={`bulk-view-${o.order_id}`} className="gap-1">
                          <Eye size={12}/>View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredOrders.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No bulk orders.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* REPORTS */}
        <TabsContent value="reports" className="mt-4 space-y-4">
          <Card className="border-border shadow-none">
            <CardContent className="p-4 flex flex-wrap items-end gap-3">
              <div className="flex gap-1">
                {["day", "month", "year"].map((p) => (
                  <button
                    key={p}
                    onClick={() => setPeriod(p)}
                    className={`px-4 py-2 rounded text-sm font-medium ${period === p ? "bg-terracotta text-white" : "bg-surface-alt"}`}
                    data-testid={`bulk-period-${p}`}
                  >{p === "day" ? "Today" : p[0].toUpperCase() + p.slice(1) + "ly"}</button>
                ))}
              </div>
              {period !== "day" && (
                <div>
                  <div className="label-overline text-muted-foreground">Year</div>
                  <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value) || now.getFullYear())} className="w-28 h-9 px-2 border border-border rounded font-mono"/>
                </div>
              )}
              {period === "month" && (
                <div>
                  <div className="label-overline text-muted-foreground">Month</div>
                  <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="h-9 px-2 border border-border rounded">
                    {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => <option key={m} value={i+1}>{m}</option>)}
                  </select>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-border shadow-none"><CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded bg-terracotta/10 flex items-center justify-center"><ShoppingBag size={18} className="text-terracotta"/></div>
              <div><div className="label-overline text-muted-foreground">Bulk Orders</div><div className="font-heading text-2xl font-bold" data-testid="bulk-rep-count">{report.order_count}</div></div>
            </CardContent></Card>
            <Card className="border-border shadow-none"><CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded bg-moss/10 flex items-center justify-center"><IndianRupee size={18} className="text-moss"/></div>
              <div><div className="label-overline text-muted-foreground">Total Revenue</div><div className="font-heading text-2xl font-bold text-terracotta" data-testid="bulk-rep-revenue">₹{Math.round(report.total_revenue)}</div></div>
            </CardContent></Card>
            <Card className="border-border shadow-none"><CardContent className="p-4">
              <div className="label-overline text-muted-foreground mb-1">By Payment Method</div>
              <div className="grid grid-cols-2 gap-1 text-xs">
                {Object.entries(report.by_method || {}).filter(([_, v]) => v > 0).map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <span className="uppercase">{k}</span>
                    <span className="font-mono font-bold">₹{Math.round(v)}</span>
                  </div>
                ))}
                {Object.values(report.by_method || {}).every((v) => v === 0) && <div className="col-span-2 text-muted-foreground">—</div>}
              </div>
            </CardContent></Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-border shadow-none">
              <CardContent className="p-0">
                <div className="p-3 border-b border-border font-heading font-bold">Products Sold</div>
                <div className="max-h-96 overflow-y-auto scroll-thin">
                  <Table>
                    <TableHeader><TableRow><TableHead>Product</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Revenue</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {report.products.map((p) => (
                        <TableRow key={p.product_id}>
                          <TableCell className="text-xs">{p.name}</TableCell>
                          <TableCell className="text-right font-mono">{p.qty}</TableCell>
                          <TableCell className="text-right font-mono text-terracotta font-bold">₹{Math.round(p.amount)}</TableCell>
                        </TableRow>
                      ))}
                      {report.products.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-6 text-muted-foreground text-sm">No data</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border shadow-none">
              <CardContent className="p-0">
                <div className="p-3 border-b border-border font-heading font-bold">{period === "year" ? "Monthly" : "Daily"} Trend</div>
                <div className="max-h-96 overflow-y-auto scroll-thin">
                  <Table>
                    <TableHeader><TableRow><TableHead>{period === "year" ? "Month" : "Day"}</TableHead><TableHead className="text-right">Orders</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {report.buckets.map((b) => (
                        <TableRow key={b.label}>
                          <TableCell className="font-mono text-xs">{b.label}</TableCell>
                          <TableCell className="text-right font-mono">{b.count}</TableCell>
                          <TableCell className="text-right font-mono">₹{Math.round(b.total)}</TableCell>
                        </TableRow>
                      ))}
                      {report.buckets.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-6 text-muted-foreground text-sm">No data</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* View order */}
      <Dialog open={!!viewOrder} onOpenChange={(o) => { if (!o) setViewOrder(null); }}>
        <DialogContent className="max-w-2xl" data-testid="bulk-view-dialog">
          <DialogHeader><DialogTitle className="font-heading">Order {viewOrder?.order_no}</DialogTitle></DialogHeader>
          {viewOrder && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3 bg-sand p-3 rounded">
                <div><div className="label-overline text-muted-foreground">Date</div>{new Date(viewOrder.created_at).toLocaleString()}</div>
                <div><div className="label-overline text-muted-foreground">Customer</div>{viewOrder.customer_name || "-"} · {viewOrder.customer_phone || "-"}</div>
                <div><div className="label-overline text-muted-foreground">Address</div>{viewOrder.customer_address || "-"}</div>
                <div><div className="label-overline text-muted-foreground">Delivery</div>{viewOrder.delivery_date || "-"}</div>
              </div>
              <div className="border border-border rounded max-h-72 overflow-y-auto scroll-thin">
                <Table>
                  <TableHeader><TableRow><TableHead>Product</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Rate</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {viewOrder.items.map((it, i) => (
                      <TableRow key={i}>
                        <TableCell>{it.name}</TableCell>
                        <TableCell className="text-right">{it.qty}</TableCell>
                        <TableCell className="text-right font-mono">₹{Math.round(it.rate)}</TableCell>
                        <TableCell className="text-right font-mono">₹{Math.round(it.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex justify-between font-bold text-lg border-t border-border pt-2">
                <span>Net Total</span>
                <span className="font-mono text-terracotta">₹{Math.round(viewOrder.net_total)}</span>
              </div>
              {viewOrder.notes && <div className="text-xs text-muted-foreground">Notes: {viewOrder.notes}</div>}
            </div>
          )}
          <DialogFooter>
            {viewOrder?.status !== "refunded" && (
              <Button onClick={() => refundOrder(viewOrder.order_id)} data-testid="bulk-refund-btn" variant="destructive" className="gap-2">
                <RotateCcw size={14}/>Refund Full Order
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
